import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { LiveKitRoom, VideoConference, RoomAudioRenderer } from "@livekit/components-react";
import "@livekit/components-styles";
import { Loader2, Video, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import DevicePreflight from "@/components/DevicePreflight";

/**
 * Interview room page.
 *
 * URL: /interview/:bookingId
 *   ?role=employer   → uses the authenticated session
 *   ?t=<reschedule_token>  → candidate join (from confirmation email)
 *
 * Flow: fetch token → device preflight (mic/cam test) → LiveKit room.
 */
export default function InterviewRoom() {
    const { bookingId } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const role = params.get("role") === "employer" ? "employer" : "candidate";
    const rescheduleToken = params.get("t") || "";

    const [state, setState] = useState({ phase: "idle", error: "", data: null });
    // "idle" | "loading" | "preflight" | "joined" | "error"
    const [devicePrefs, setDevicePrefs] = useState(null);

    const fetchToken = useCallback(async () => {
        try {
            setState({ phase: "loading", error: "", data: null });
            const { data } = await api.post("/livekit/token", {
                booking_id: bookingId,
                role,
                reschedule_token: rescheduleToken || null,
            });
            setState({ phase: "preflight", error: "", data });
        } catch (err) {
            setState({ phase: "error", error: formatApiError(err), data: null });
        }
    }, [bookingId, role, rescheduleToken]);

    useEffect(() => { fetchToken(); }, [fetchToken]);

    const onDisconnected = () => {
        if (role === "employer") navigate("/employer/scheduler");
        else navigate("/");
    };

    if (state.phase === "loading" || state.phase === "idle") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950" data-testid="interview-loading">
                <div className="text-center">
                    <Loader2 size={32} className="mx-auto animate-spin text-emerald-400" />
                    <p className="mt-3 text-slate-300 text-sm">Connecting to the interview room…</p>
                </div>
            </div>
        );
    }
    if (state.phase === "error") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6" data-testid="interview-error">
                <div className="max-w-md w-full bg-slate-900 rounded-2xl p-8 text-center border border-slate-800">
                    <div className="mx-auto w-14 h-14 rounded-full bg-rose-500/10 flex items-center justify-center mb-4">
                        <AlertCircle size={22} className="text-rose-500" />
                    </div>
                    <h1 className="font-heading font-medium text-xl text-white">Can&apos;t join right now</h1>
                    <p className="text-sm text-slate-400 mt-2">{state.error}</p>
                    <div className="mt-6 flex gap-2 justify-center">
                        <Button variant="outline" onClick={fetchToken} data-testid="interview-retry">Try again</Button>
                        <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-400"><ArrowLeft size={14} className="mr-1" /> Home</Button>
                    </div>
                </div>
            </div>
        );
    }

    // Preflight step
    if (state.phase === "preflight") {
        return (
            <DevicePreflight
                name={state.data.display_name}
                onCancel={() => navigate(role === "employer" ? "/employer/scheduler" : "/")}
                onReady={(prefs) => {
                    setDevicePrefs(prefs);
                    setState((s) => ({ ...s, phase: "joined" }));
                }}
            />
        );
    }

    // Ready — mount the LiveKit room with the device preferences from preflight.
    const { token, url, display_name } = state.data;
    return (
        <div className="fixed inset-0 bg-slate-950" data-testid="interview-room">
            <div className="absolute top-3 left-4 z-50 flex items-center gap-2 bg-slate-900/80 backdrop-blur border border-slate-800 rounded-full px-3 py-1.5">
                <Video size={14} className="text-emerald-400" />
                <span className="text-xs text-slate-200">Interview · joined as {display_name}</span>
            </div>
            <LiveKitRoom
                token={token}
                serverUrl={url}
                connect
                audio={devicePrefs?.audioEnabled ? (devicePrefs.audioDeviceId ? { deviceId: { ideal: devicePrefs.audioDeviceId } } : true) : false}
                video={devicePrefs?.videoEnabled ? (devicePrefs.videoDeviceId ? { deviceId: { ideal: devicePrefs.videoDeviceId } } : true) : false}
                onDisconnected={onDisconnected}
                options={{ adaptiveStream: true, dynacast: true }}
                data-lk-theme="default"
                className="h-full w-full"
            >
                <VideoConference />
                <RoomAudioRenderer />
            </LiveKitRoom>
        </div>
    );
}
